import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Clock, CheckCircle, ArrowRight, Users } from 'lucide-react';
import axios from 'axios';
import { PageSEO } from '../components/SEO';

/**
 * Kontakt - TasteSkill Premium Redesign
 * 
 * Verbesserungen:
 * - Professionelleres Layout
 * - Bessere visuelle Hierarchie
 * - Klickbare Kontaktdaten
 * - Verbesserte Erfolgsbestätigung
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Kontakt = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'beratung',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 201) {
        setIsSubmitted(true);
        toast({
          title: "Anfrage erfolgreich gesendet!",
          description: "Wir melden uns in Kürze bei Ihnen.",
        });
        
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            subject: 'beratung',
            message: '',
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: "destructive",
        title: "Fehler beim Senden",
        description: error.response?.data?.detail || "Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageSEO 
        title="Kontakt – Beratung zu GmbH-Kauf und -Gründung | Firmbay"
        description="Kontaktieren Sie Firmbay für eine persönliche Beratung zu GmbH-Kauf, -Verkauf oder -Gründung. Wir sind für Sie da: info@firmbay.de oder 0170-7967959."
        path="/kontakt"
      />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E9F7F6] rounded-full mb-4">
              <Users className="w-3.5 h-3.5 text-[#009E9A]" />
              <span className="text-[11px] font-semibold text-[#009E9A] uppercase tracking-wider">
                Persönliche Beratung
              </span>
            </div>
            <h1 className="text-[28px] md:text-[34px] font-bold text-[#0B1930] mb-3 leading-tight tracking-[-0.02em]">
              Kontakt aufnehmen
            </h1>
            <p className="text-base text-gray-600 leading-relaxed">
              Haben Sie Fragen zu GmbH-Kauf, Verkauf oder Gründung? 
              Wir beraten Sie persönlich und unverbindlich.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-3 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6] border-y border-[#009E9A]/10">
        <div className="max-w-[1280px] mx-auto">
          <p className="text-xs text-gray-600 leading-relaxed">
            <strong className="text-[#0B1930]">Hinweis:</strong> Firmbay bietet keine Rechts- oder Steuerberatung an. 
            Die Plattform dient der Vermittlung und organisatorischen Begleitung. 
            Gesellschaftsrechtliche Vorgänge erfolgen durch Notare und externe Partner.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-bold text-[#0B1930] mb-6">Senden Sie uns eine Nachricht</h2>
                
                {isSubmitted ? (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 bg-[#E9F7F6] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-[#009E9A]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1930] mb-2">
                      Vielen Dank für Ihre Anfrage!
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      Wir haben Ihre Nachricht erhalten und melden uns in der Regel innerhalb von 24 Stunden.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                          Name <span className="text-[#009E9A]">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A]"
                          placeholder="Ihr vollständiger Name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                          E-Mail <span className="text-[#009E9A]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A]"
                          placeholder="ihre@email.de"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A]"
                          placeholder="+49 123 456789"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                          Unternehmen
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A]"
                          placeholder="Ihr Unternehmen (optional)"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                        Betreff <span className="text-[#009E9A]">*</span>
                      </label>
                      <select
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A] bg-white"
                      >
                        <option value="beratung">Allgemeine Beratung</option>
                        <option value="marktplatz">Bestands-GmbH kaufen</option>
                        <option value="verkauf">GmbH verkaufen</option>
                        <option value="gmbh-gruendung">GmbH-Gründung</option>
                        <option value="sonstiges">Sonstiges</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0B1930] mb-1.5">
                        Ihre Nachricht <span className="text-[#009E9A]">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A] resize-none"
                        placeholder="Beschreiben Sie Ihr Anliegen..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0B1930] hover:bg-[#162844] text-white py-3 rounded-lg text-sm font-semibold"
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                      {!isSubmitting && <ArrowRight className="ml-2 w-4 h-4" />}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-5">
              {/* Contact Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-bold text-[#0B1930] mb-5">Direkter Kontakt</h3>
                <div className="space-y-4">
                  <a href="mailto:info@firmbay.de" className="flex items-start gap-3 group">
                    <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#009E9A]" />
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide">E-Mail</div>
                      <div className="text-sm font-semibold text-[#0B1930] group-hover:text-[#009E9A] transition-colors">
                        info@firmbay.de
                      </div>
                    </div>
                  </a>
                  <a href="tel:+491707967959" className="flex items-start gap-3 group">
                    <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-[#009E9A]" />
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide">Telefon</div>
                      <div className="text-sm font-semibold text-[#0B1930] group-hover:text-[#009E9A] transition-colors">
                        0170-7967959
                      </div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-[#009E9A]" />
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide">Adresse</div>
                      <div className="text-sm font-semibold text-[#0B1930]">
                        Hansastr. 72<br />44137 Dortmund
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-[#009E9A]" />
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide">Erreichbarkeit</div>
                      <div className="text-sm font-semibold text-[#0B1930]">
                        Mo–Fr: 09:00–18:00 Uhr
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-[#E9F7F6] border border-[#009E9A]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#0B1930] mb-1.5 text-sm">Schnelle Antwort</h3>
                <p className="text-sm text-gray-600">
                  Wir antworten in der Regel innerhalb von 24 Stunden während unserer Geschäftszeiten.
                </p>
              </div>

              {/* Quick Links */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-[#0B1930] mb-3 text-sm">Direktlinks</h3>
                <div className="space-y-2">
                  <Link to="/marktplatz" className="flex items-center justify-between text-sm text-gray-600 hover:text-[#009E9A] transition-colors">
                    <span>GmbH kaufen</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/seller-onboarding" className="flex items-center justify-between text-sm text-gray-600 hover:text-[#009E9A] transition-colors">
                    <span>GmbH verkaufen</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/onboarding" className="flex items-center justify-between text-sm text-gray-600 hover:text-[#009E9A] transition-colors">
                    <span>GmbH gründen</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Kontakt;
